import { useEffect, useState, type JSX } from 'react';
import type { EffectProps } from './types';
import { markEffectActivated, isEffectActivated } from './effectRegistry';

export function MessageRewrite({ originalContent, params, trigger, triggerDelayMs, onComplete, children, effectKey }: EffectProps): JSX.Element {
  const alreadyActivated = effectKey ? isEffectActivated(effectKey) : false;
  const rewriteText = (params.rewriteText as string) ?? originalContent.text;

  const [phase, setPhase] = useState<'original' | 'transition' | 'rewritten'>(alreadyActivated ? 'rewritten' : 'original');
  const [displayText, setDisplayText] = useState(alreadyActivated ? rewriteText : originalContent.text);

  const rewriteDelay = (params.rewriteDelayMs as number) ?? 3000;
  const transitionType = (params.transitionType as string) ?? 'glitch';
  const transitionMs = (params.transitionDurationMs as number) ?? 500;

  useEffect(() => {
    if (alreadyActivated) return;
    const delay = (trigger === 'after_delay' ? triggerDelayMs : 0) + rewriteDelay;
    const timer = setTimeout(() => setPhase('transition'), delay);
    return () => clearTimeout(timer);
  }, [trigger, triggerDelayMs, rewriteDelay, alreadyActivated]);

  useEffect(() => {
    if (phase !== 'transition') return;

    if (transitionType === 'typewriter') {
      // Typewriter: erase then type new text
      let charIndex = 0;
      const perCharMs = transitionMs / (rewriteText.length + originalContent.text.length);
      const eraseInterval = setInterval(() => {
        const eraseLen = originalContent.text.length - charIndex;
        if (eraseLen > 0) {
          setDisplayText(originalContent.text.slice(0, eraseLen));
          charIndex++;
        } else {
          clearInterval(eraseInterval);
          let typeIndex = 0;
          const typeInterval = setInterval(() => {
            typeIndex++;
            setDisplayText(rewriteText.slice(0, typeIndex));
            if (typeIndex >= rewriteText.length) {
              clearInterval(typeInterval);
              setPhase('rewritten');
              if (effectKey) markEffectActivated(effectKey);
              onComplete?.();
            }
          }, perCharMs);
        }
      }, perCharMs);
      return () => clearInterval(eraseInterval);
    }

    // Default: glitch/fade - instant switch with CSS transition
    const timer = setTimeout(() => {
      setDisplayText(rewriteText);
      setPhase('rewritten');
      if (effectKey) markEffectActivated(effectKey);
      onComplete?.();
    }, transitionMs);
    return () => clearTimeout(timer);
  }, [phase, transitionType, transitionMs, rewriteText, originalContent.text, onComplete, effectKey]);

  if (phase === 'original') {
    return <>{children}</>;
  }

  const transitionClass = phase === 'transition'
    ? `sn-rewrite sn-rewrite--${transitionType}`
    : 'sn-rewrite sn-rewrite--done';

  return (
    <div className={transitionClass} style={{ position: 'relative' }}>
      <div style={{ visibility: 'hidden' }}>{children}</div>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          padding: 'inherit'
        }}
      >
        <span>{displayText}</span>
      </div>
    </div>
  );
}
